import { motion } from "framer-motion";
import { useLanguage } from "@/context/LanguageContext";
import { scrollToId } from "@/lib/scroll";
import { StreamingLinks } from "@/components/StreamingLinks";

const MaskLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const JustifiedLine = ({ text, delay = 0, accent = false }) => (
  <span className="block overflow-hidden">
    <motion.span
      className={`flex justify-between w-full ${accent ? "text-gold" : ""}`}
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {text.split("").map((ch, i) => (
        <span key={i}>{ch}</span>
      ))}
    </motion.span>
  </span>
);

export const Hero = () => {
  const { t } = useLanguage();

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden"
    >
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
        className="absolute inset-0 lg:left-[42%]"
      >
        <img
          src="/assets/hero-portrait.webp"
          alt="Giovanni Costello"
          data-testid="hero-portrait-slot"
          className="w-full h-full object-cover object-[32%_top]"
        />
        <div className="absolute inset-0 bg-ink/72 lg:bg-transparent" />
        <div className="absolute inset-0 lg:bg-[linear-gradient(90deg,#0B0B0C_0%,rgba(11,11,12,0.75)_25%,rgba(11,11,12,0.25)_55%,transparent_100%)]" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-transparent to-ink/30" />
      </motion.div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 pt-[72px]">
        <div className="max-w-3xl py-24">
          <MaskLine delay={0.15}>
            <span className="gc-eyebrow">{t.hero.kicker}</span>
          </MaskLine>

          <h1 className="mt-6 font-serif uppercase leading-[0.95] text-ivory text-5xl sm:text-7xl lg:text-8xl">
            <JustifiedLine text="Giovanni" delay={0.3} />
            <JustifiedLine text="Costello" delay={0.45} accent />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.9 }}
            className="mt-8 max-w-xl text-base sm:text-lg text-ash font-light leading-relaxed"
          >
            {t.hero.lead}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.9 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-music"
              onClick={() => scrollToId("albums")}
              className="px-8 py-3.5 bg-gold text-ivory text-[11px] tracking-[0.25em] uppercase font-semibold hover:bg-champagne hover:text-ink transition-colors duration-300"
            >
              {t.hero.ctaMusic}
            </button>
            <button
              data-testid="hero-cta-live"
              onClick={() => scrollToId("contact")}
              className="px-8 py-3.5 border border-ivory/25 text-ivory text-[11px] tracking-[0.25em] uppercase hover:border-gold hover:text-gold transition-colors duration-300"
            >
              {t.hero.ctaBook}
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2, duration: 1 }}
            className="mt-12"
          >
            <StreamingLinks variant="hero" />
          </motion.div>
        </div>
      </div>

      <motion.button
        data-testid="hero-scroll-indicator"
        onClick={() => scrollToId("booking")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-dim hover:text-gold transition-colors"
      >
        <span className="text-[10px] tracking-[0.3em] uppercase">{t.hero.scroll}</span>
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 2 }} className="w-px h-8 bg-gradient-to-b from-gold/70 to-transparent" />
      </motion.button>
    </section>
  );
};
